/**
 * Algorithm 1: Question Score Calculation
 * Calculate individual question mastery (0-100%)
 * 
 * @param {Object} questionData - User's performance data for this question
 * @param {number} questionData.timesShown - How many times question was shown
 * @param {number} questionData.timesCorrect - How many times answered correctly
 * @param {number} questionData.hintsUsed - Total hints used
 * @param {number} questionData.lastShown - Timestamp of last showing
 * @param {number} questionData.streak - Consecutive correct answers
 * @param {number} questionDifficulty - Difficulty rating (0.0-1.0)
 * @returns {number} - Question score (0-100)
 */

import { SCORING } from '@config/constants.js';

export function calculateQuestionScore(questionData, questionDifficulty) {
  const { timesShown, timesCorrect, hintsUsed, lastShown, streak } = questionData;

  // New question - no score yet
  if (timesShown === 0) return 0;

  const correctRate = timesCorrect / timesShown;

  // Step 1: Base score from correct rate
  // Capped at 70 to leave headroom for the difficulty bonus below.
  let score = correctRate * 70;

  // Step 2: Difficulty bonus (harder questions earn more points)
  // Bonus is proportional to correctRate so it is only earned when the learner
  // is actually answering correctly.
  // DIFFICULTY_BONUS_SCALE (60) sets the available bonus range so that with
  // DIFFICULTY_MULTIPLIER=0.5 the max bonus is: 1.0 × 0.8 × 0.5 × 60 = 24 pts
  //   Easy (0.2) → up to +6 pts; Medium (0.5) → up to +15 pts; Hard (0.8) → up to +24 pts
  const DIFFICULTY_BONUS_SCALE = 60;
  const difficultyBonus = correctRate * questionDifficulty * SCORING.DIFFICULTY_MULTIPLIER * DIFFICULTY_BONUS_SCALE;
  score += difficultyBonus;

  // Step 3: Hint penalty
  // Each hint used reduces score by HINT_PENALTY_PER_USE points, capped at MAX_HINT_PENALTY.
  const hintPenalty = Math.min(hintsUsed * SCORING.HINT_PENALTY_PER_USE, SCORING.MAX_HINT_PENALTY);
  score -= hintPenalty;

  // Step 4: Consistency bonus
  // 3+ correct in a row earns a bonus scaled by difficulty to preserve ordering.
  // With CONSISTENCY_BONUS=15: Easy (0.2) → +3 pts; Medium (0.5) → +7.5 pts; Hard (0.8) → +12 pts
  if (streak >= SCORING.CONSISTENCY_THRESHOLD) {
    score += SCORING.CONSISTENCY_BONUS * questionDifficulty;
  }

  // Step 5: Time decay
  // If not seen recently, confidence drops
  const hoursSinceLastSeen = (Date.now() - lastShown) / (1000 * 60 * 60);
  const decayPenalty = Math.min(hoursSinceLastSeen * SCORING.TIME_DECAY_RATE, SCORING.MAX_TIME_DECAY);
  score -= decayPenalty;

  // Step 6: Clamp to 0-100
  return Math.max(0, Math.min(100, Math.round(score)));
}
